import { Component, OnInit, OnDestroy, AfterViewChecked, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApplicationService } from 'app/shared/services/app/application/application.service';
import { Application } from 'app/shared/services/app/application/application.model';
import { Question } from 'app/shared/services/app/content/models';
import { Session } from 'app/shared/services/session/session.service';
import { Toast } from 'app/shared/components/toast/toast.service';
import { QuestionBuilderComponent } from 'app/shared/components/question-builder/question-builder.component';
import { LoaderButton } from 'app/shared/components/loader-button/loader-button.component';
import { DragulaService } from 'ng2-dragula/ng2-dragula';

@Component({
  selector: 'tog-questions-editor',
  templateUrl: './questions-editor.component.html',
  styleUrls: ['./questions-editor.component.scss'],
  providers: [DragulaService]
})
export class QuestionsEditorComponent implements OnInit, AfterViewChecked, OnDestroy {
  app: Application;
  subscriptions: any[] = [];
  expandedQuestion: Question;
  questionToDelete: number;
  questionToEdit: Question;
  creatingQuestion: boolean = false;
  multipleChoiceLetters: string[] = ['A', 'B', 'C', 'D'];

  // For switching between editing one question to another
  shouldCreateQuestion: boolean = false;
  nextQuestionToEdit: Question;
  showCancelQuestionModal: EventEmitter<any> = new EventEmitter<any>();

  questionsChanged: boolean = false;
  @Output() questionsUpdated: EventEmitter<any> = new EventEmitter();

  @Input() allowFeedback: boolean = true;
  @Input() questions: Question[] = [];

  @ViewChild('confirmDeleteQuestionButton') confirmDeleteQuestionButton: ElementRef;
  @ViewChild(QuestionBuilderComponent) questionBuilder: QuestionBuilderComponent;

  @ViewChild('deleteQuestionLoaderButton') deleteQuestionLoaderButton: LoaderButton;

  constructor(private _fb: FormBuilder,
    private _session: Session,
    private _el: ElementRef,
    private _as: ApplicationService,
    private _toast: Toast,
    private _ds: DragulaService) {
    this._session.user().then(() => {
      return this._as.getApplicationDetails(this._session.selectedAppSysName);
    })
      .then((app: Application) => {
        this.app = app;
      });
  }

  ngOnInit() {
    this._ds.setOptions('questions', {
      moves: (el: Element, target: Element, source: Element, sibling: Element): boolean => {
        return !el.classList.contains('no-drag');
      },
      accepts: (el: Element, target: Element, source: Element, sibling: Element): boolean => {
        return !el.contains(target);
      }
    });
    this.subscriptions.push(this._ds.drop.subscribe(() => {
      this.questionsChanged = true;
    }));
  }

  // content does not update immediately after drag-n-drop, so this handles it
  ngAfterViewChecked() {
    if (this.questionsChanged) {
      this.questionsUpdated.emit(this.questions);
      this.questionsChanged = false;
    }
  }

  launchAddQuestion() {
    if (this.questionToEdit) {
      this.shouldCreateQuestion = true;
      this.questionBuilder.cancelAddingQuestion();
    } else {
      this.creatingQuestion = true;
    }
  }

  // User finished editing a question. Reset next question to edit and current
  // editing question.
  onEditQuestion() {
    this.nextQuestionToEdit = null;
    this.questionToEdit = null;
    this.questionsUpdated.emit(this.questions);
  }

  // User finished creating a question.
  onCreateQuestion(question: Question) {
    this.creatingQuestion = false;
    this.questions.push(question);
    this.questionsUpdated.emit(this.questions);
  }

  // User confirmed they wanted to edit a question. If the user received this popup
  // after trying to edit or create that question, show the correct builder.
  onQuestionBuilderCancel() {
    this.questionToEdit = null;
    this.creatingQuestion = false;
    if (this.nextQuestionToEdit) {
      this.questionToEdit = this.nextQuestionToEdit;
      this.creatingQuestion = false;
      this.nextQuestionToEdit = null;
    } else if (this.shouldCreateQuestion) {
      this.questionToEdit = null;
      this.shouldCreateQuestion = false;
      setTimeout((self) => {
        self.creatingQuestion = true;
      }, 0, this);
    }
  }

  // Show question answer details.
  expandQuestion(question: Question) {
    if (question === this.expandedQuestion) {
      this.expandedQuestion = null;
    } else if (question !== this.questionToEdit) {
      this.expandedQuestion = question;
    }
  }

  // Check to see if we are currently editing or creating a question. If so,
  // update the temporary variables and show the confirmation popup for the current
  // question builder.
  editQuestion(e: Event, question: Question) {
    e.stopPropagation();
    if (question === this.expandedQuestion) this.expandedQuestion = null;
    if (this.creatingQuestion || this.questionToEdit) {
      this.nextQuestionToEdit = question;
      this.questionBuilder.cancelAddingQuestion();
    } else {
      this.questionToEdit = question;
      this.nextQuestionToEdit = null;
    }
    this.questionsUpdated.emit(this.questions);
  }

  // User decided they did not want to switch questions. Reset.
  doNotSwitchQuestions() {
    this.nextQuestionToEdit = null;
    this.shouldCreateQuestion = false;
  }

  deleteQuestion(e: Event, questionIndex: number) {
    e.stopPropagation();
    this.questionToDelete = questionIndex;
    this.confirmDeleteQuestionButton.nativeElement.click();
  }

  confirmDeleteQuestion() {
    if (this.questionToEdit === this.questions[this.questionToDelete]) this.questionToEdit = null;
    this.questions.splice(this.questionToDelete, 1);
    this.questionsUpdated.emit(this.questions);
    this.deleteQuestionLoaderButton.setConfirmed();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/questions-editor/questions-editor.component.ts